import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, Upload, X, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const CATEGORIES = [
  { value: 'professionalism', label: 'Unprofessional / disrespectful' },
  { value: 'no_show', label: 'No-show' },
  { value: 'left_early', label: 'Left the booking early' },
  { value: 'late', label: 'Arrived late' },
  { value: 'dress_code', label: 'Dress code / preparation' },
  { value: 'solicitation', label: 'Asked for tips / food / money / favors' },
  { value: 'off_platform', label: 'Tried to take work off-platform' },
  { value: 'quality', label: 'Quality of services' },
  { value: 'property', label: 'Property damage / missing items' },
  { value: 'impaired', label: 'Appeared impaired / intoxicated' },
  { value: 'harassment', label: 'Harassment / inappropriate behavior' },
  { value: 'safety', label: 'Safety concern / incident' },
  { value: 'other', label: 'Something else' },
];

export const ReportConcernModal = ({ open, onClose, bookingId, talentName }) => {
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory(''); setSeverity('medium'); setDescription(''); setAttachments([]);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploading(true);
    try {
      const sigRes = await api.post('/cloudinary/signature', { folder: 'client_concerns', resource_type: 'image' });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;
      const fd = new FormData();
      fd.append('file', file); fd.append('signature', signature);
      fd.append('timestamp', timestamp); fd.append('api_key', api_key);
      fd.append('folder', 'client_concerns');
      const up = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: 'POST', body: fd });
      const data = await up.json();
      if (!data.secure_url) throw new Error('Upload failed');
      setAttachments((a) => [...a, data.secure_url]);
      toast.success('Attachment added');
    } catch (err) {
      toast.error('Could not upload that file');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!category) { toast.error('Please choose what the concern is about'); return; }
    if (!description.trim()) { toast.error('Please describe what happened'); return; }
    setSubmitting(true);
    try {
      await api.post(`/client/bookings/${bookingId}/report-concern`, {
        category, severity, description: description.trim(), attachments,
      });
      toast.success('Concern submitted — the iEvntür team will review it.');
      reset();
      onClose?.(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit your concern');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) { reset(); onClose?.(false); } }}>
      <DialogContent className="max-w-md" data-testid="report-concern-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600" /> Report a Concern
          </DialogTitle>
          <DialogDescription>
            {talentName ? <>About <span className="font-semibold text-slate-700">{talentName}</span> on this booking. </> : null}
            Your report goes to the iEvntür team for review — it is not shared with the talent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600">What is the concern about?</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="concern-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">How serious is it?</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="concern-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low — minor issue</SelectItem>
                <SelectItem value="medium">Medium — needs attention</SelectItem>
                <SelectItem value="high">High — serious</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">What happened?</Label>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what happened, including any relevant details."
              data-testid="concern-description"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">Supporting documentation (optional)</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {attachments.map((url, i) => (
                <div key={i} className="relative">
                  <img src={url} alt="attachment" className="w-14 h-14 rounded object-cover border" />
                  <button type="button" onClick={() => setAttachments((a) => a.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-white rounded-full border shadow p-0.5" data-testid={`concern-remove-attachment-${i}`}>
                    <X className="w-3 h-3 text-slate-500" />
                  </button>
                </div>
              ))}
              <label className="w-14 h-14 rounded border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-amber-400" data-testid="concern-upload-label">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Upload className="w-4 h-4 text-slate-400" />}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="concern-file-input" />
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose?.(false); }} disabled={submitting} data-testid="concern-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={submitting || uploading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="concern-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Submit Concern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportConcernModal;
